/**
 * useStitchDirty — issue #863, epic #831.
 *
 * Tracks whether the user has changed stitch bindings since the last
 * E2E flow generation. Used by `<GenerateFlowsButton>` to surface the
 * "stitches changed — regenerate flows" banner on StitchReviewPage and
 * the Generate CTA on ProjectPage.
 *
 * Persistence: sessionStorage keyed by projectId so a tab refresh
 * keeps the dirty banner visible. Cleared on successful generation.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'veodiagram:stitch-dirty:';

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

function readDirty(projectId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(storageKey(projectId)) === '1';
  } catch {
    return false;
  }
}

function writeDirty(projectId: string, dirty: boolean): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (dirty) {
      sessionStorage.setItem(storageKey(projectId), '1');
    } else {
      sessionStorage.removeItem(storageKey(projectId));
    }
  } catch {
    /* ignore storage failures */
  }
}

export interface UseStitchDirtyResult {
  isDirty: boolean;
  markDirty: () => void;
  clearDirty: () => void;
}

export function useStitchDirty(projectId: string | undefined): UseStitchDirtyResult {
  const [isDirty, setIsDirty] = useState<boolean>(() =>
    projectId ? readDirty(projectId) : false,
  );

  // Re-read on projectId change.
  useEffect(() => {
    if (!projectId) {
      setIsDirty(false);
      return;
    }
    setIsDirty(readDirty(projectId));
  }, [projectId]);

  // Cross-tab sync: another tab editing stitches in the same project
  // should also flip our banner. Listen for storage events.
  useEffect(() => {
    if (!projectId) return;
    const handler = (e: StorageEvent) => {
      if (e.key !== storageKey(projectId)) return;
      setIsDirty(e.newValue === '1');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [projectId]);

  const markDirty = useCallback(() => {
    if (!projectId) return;
    writeDirty(projectId, true);
    setIsDirty(true);
  }, [projectId]);

  const clearDirty = useCallback(() => {
    if (!projectId) return;
    writeDirty(projectId, false);
    setIsDirty(false);
  }, [projectId]);

  return { isDirty, markDirty, clearDirty };
}
